import type React from 'react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { getCurrentUserFromApi } from '@/api/keycloak-auth';
import keycloakService, { type User } from '@/services/keycloak.service';

export interface KeycloakAuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  getUserRoles: () => string[];
  getToken: () => string | undefined;
  updateToken: (minValidity?: number) => Promise<boolean>;
  getAccountManagementUrl: () => string;
}

const KeycloakAuthContext = createContext<KeycloakAuthContextType | undefined>(undefined);

export const useKeycloakAuth = (): KeycloakAuthContextType => {
  const context = useContext(KeycloakAuthContext);
  if (context === undefined) {
    throw new Error('useKeycloakAuth must be used within a KeycloakAuthProvider');
  }
  return context;
};

interface KeycloakAuthProviderProps {
  children: ReactNode;
}

export const KeycloakAuthProvider: React.FC<KeycloakAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize Keycloak on mount
  useEffect(() => {
    let mounted = true;

    const initKeycloak = async () => {
      try {
        setIsLoading(true);

        // Initialize Keycloak
        const authenticated = await keycloakService.init();

        if (!mounted) return;

        setIsInitialized(true);
        setIsAuthenticated(authenticated);

        if (authenticated) {
          const currentUser = keycloakService.getCurrentUser();
          setUser(currentUser);
          getCurrentUserFromApi().then((apiUser) => {
            if (!mounted) return;
            if (apiUser?.partyId && currentUser) {
              setUser((prev) => (prev ? { ...prev, partyId: apiUser.partyId } : null));
            }
          });
        }
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: using in web-app
        console.error('Keycloak auth context initialization error:', error);
        if (mounted) {
          setIsInitialized(true); // Set to true so UI can proceed even on error
          setIsAuthenticated(false);
          setUser(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initKeycloak();

    return () => {
      mounted = false;
    };
  }, []);

  // Set up Keycloak event listeners
  useEffect(() => {
    if (!isInitialized) return;

    const keycloak = keycloakService.getKeycloak() as unknown as {
      onAuthSuccess: null | (() => void);
      onAuthError: null | ((error: unknown) => void);
      onAuthRefreshSuccess: null | (() => void);
      onAuthRefreshError: null | (() => void);
      onAuthLogout: null | (() => void);
    };

    const handleAuthSuccess = () => {
      setIsAuthenticated(true);
      const currentUser = keycloakService.getCurrentUser();
      setUser(currentUser);
      getCurrentUserFromApi().then((apiUser) => {
        if (apiUser?.partyId) {
          setUser((prev) => (prev ? { ...prev, partyId: apiUser.partyId } : null));
        }
      });
    };

    const handleAuthError = (_error: unknown) => {
      setIsAuthenticated(false);
      setUser(null);
    };

    const handleAuthRefreshSuccess = () => {
      const currentUser = keycloakService.getCurrentUser();
      setUser(currentUser);
      getCurrentUserFromApi().then((apiUser) => {
        if (apiUser?.partyId) {
          setUser((prev) => (prev ? { ...prev, partyId: apiUser.partyId } : null));
        }
      });
    };

    const handleAuthRefreshError = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    const handleAuthLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
    };

    // Add event listeners
    keycloak.onAuthSuccess = handleAuthSuccess;
    keycloak.onAuthError = handleAuthError;
    keycloak.onAuthRefreshSuccess = handleAuthRefreshSuccess;
    keycloak.onAuthRefreshError = handleAuthRefreshError;
    keycloak.onAuthLogout = handleAuthLogout;

    return () => {
      // Clean up event listeners by setting them to null
      keycloak.onAuthSuccess = null;
      keycloak.onAuthError = null;
      keycloak.onAuthRefreshSuccess = null;
      keycloak.onAuthRefreshError = null;
      keycloak.onAuthLogout = null;
    };
  }, [isInitialized]);

  const login = useCallback(async (): Promise<void> => {
    if (!isInitialized) {
      throw new Error('Keycloak not initialized');
    }

    try {
      await keycloakService.login();
    } catch (_error) {
      throw _error as Error;
    }
  }, [isInitialized]);

  const logout = useCallback(async (): Promise<void> => {
    if (!isInitialized) {
      return;
    }

    try {
      await keycloakService.logout();
    } catch (_error) {
      // Even if logout fails, clear local state
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [isInitialized]);

  const hasRole = useCallback(
    (role: string): boolean => {
      if (!isInitialized || !isAuthenticated) {
        return false;
      }
      return keycloakService.hasRole(role);
    },
    [isInitialized, isAuthenticated]
  );

  const getUserRoles = useCallback((): string[] => {
    if (!isInitialized || !isAuthenticated) {
      return [];
    }
    return keycloakService.getUserRoles();
  }, [isInitialized, isAuthenticated]);

  const getToken = useCallback((): string | undefined => {
    if (!isInitialized || !isAuthenticated) {
      return undefined;
    }
    return keycloakService.getToken();
  }, [isInitialized, isAuthenticated]);

  const updateToken = useCallback(
    async (minValidity: number = 30): Promise<boolean> => {
      if (!isInitialized) {
        return false;
      }
      return await keycloakService.updateToken(minValidity);
    },
    [isInitialized]
  );

  const getAccountManagementUrl = useCallback((): string => {
    if (!isInitialized) {
      return '';
    }
    return keycloakService.getAccountManagementUrl();
  }, [isInitialized]);

  const value: KeycloakAuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    isInitialized,
    login,
    logout,
    hasRole,
    getUserRoles,
    getToken,
    updateToken,
    getAccountManagementUrl,
  };

  return <KeycloakAuthContext.Provider value={value}>{children}</KeycloakAuthContext.Provider>;
};
